import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  reducerUserReportsInputValues,
  reducerPending,
} from "../../../redux/reducer/userReports/reducers/userReports";
import { actionGetUserReports } from "../../../redux/action/userReports/actionUserReports";
import { AppDispatch } from "../../../redux/reducer/store";

import CircleLoader from "../../../components/custom/circleLoader/circleLoader";
import SectionContent from "../../../components/sectionContent/sectionContent";
import UserReports from "../../../pages/userReports/userReports/userReports";
import HeaderBtn from "../../../components/headerBtn/headerBtn";
import Sidebar from "../../../components/sidebar/Sidebar";

import "../../../scss/App.scss";
import { IUserOptions } from "../../../types/interfaces";

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

const CustomerReportsSection = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { data, isOpenSidebar, contractsLength } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const { reportsCount, reportsInputValues, reportContracts, limit, page } =
    useSelector((state: any) => state.userReports.userReports);

  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const headFilterRef = useRef<any>(null);

  const [overflowedBtnsCount, setOverflowedBtnsCount] = useState<number>(0);
  const [isOpenGroupBtn, setIsOpenGroupBtn] = useState<boolean>(false);

  const sidebarWidth = isOpenSidebar ? 190 : 44;
  const headerFiltersAndSelectWidth = 365;
  const minEmptyWidthForNewBtn = 355;
  const groupBtnWidth = 70;
  const buttonWidth = 135;

  const availableButtons = [
    {
      component: (
        <HeaderBtn
          content="Настройки"
          purpose="customerReportsSettings"
          modalTitle="Настройки"
        />
      ),
    },
    !data?.settings?.onlyPhotoAndGeo && {
      component: (
        <HeaderBtn
          content="Выгрузить данные"
          purpose="unloadCustomerReportsData"
          modalTitle="Выгрузить данные"
          hasIcon="excel"
        />
      ),
    },
    !data?.settings?.onlyPhotoAndGeo && {
      component: (
        <HeaderBtn
          content="Выгрузка по отчетам"
          purpose="unloadCustomerReports"
          modalTitle="Выгрузка по отчетам"
          hasIcon="excel"
        />
      ),
    },
    reportsInputValues &&
      reportsInputValues.fromDate &&
      reportsInputValues.toDate &&
      (reportsInputValues.contracts?.length === 1 || contractsLength < 2) &&
      data?.settings?.periodOsa && {
        component: (
          <HeaderBtn
            content="OSA за период"
            purpose="osa"
            modalTitle="OSA за период"
            hasIcon="excel"
          />
        ),
      },
    reportsInputValues &&
      reportsInputValues.fromDate &&
      reportsInputValues.toDate &&
      (reportsInputValues.contracts?.length === 1 || contractsLength < 2) &&
      !data?.settings?.onlyPhotoAndGeo && {
        component: (
          <HeaderBtn
            content="Выгрузить отчет"
            purpose="unloadClientReport"
            modalTitle="Сводный отчет"
            hasIcon="excel"
          />
        ),
      },
    reportsInputValues &&
      reportsInputValues.fromDate &&
      reportsInputValues.toDate &&
      (reportsInputValues.contracts?.length === 1 || contractsLength < 2) &&
      !data?.settings?.onlyPhotoAndGeo && {
        component: (
          <>
            <HeaderBtn
              content="Выгрузить гор. линию"
              purpose="unloadHotline"
              modalTitle="Сводный отчет"
              hasIcon="excel"
            />
          </>
        ),
      },
    reportsInputValues &&
      reportsInputValues.fromDate &&
      reportsInputValues.toDate &&
      (reportsInputValues.contracts?.length === 1 || contractsLength < 2) &&
      data?.settings?.averageFacing && {
        component: (
          <>
            <HeaderBtn
              content="Средний фейсинг"
              purpose="averageFacing"
              modalTitle="Средний фейсинг"
              hasIcon="excel"
            />
          </>
        ),
      },
    data?.settings?.shareOfShelf &&
      (reportsInputValues.contracts?.length >= 1 || contractsLength < 2) && {
        component: (
          <>
            <HeaderBtn
              content="Доля полки"
              purpose="shelfShare"
              modalTitle="Доля полки"
              hasIcon="excel"
            />
          </>
        ),
      },
    {
      component: (
        <>
          <HeaderBtn
            content="Выгрузить фото"
            purpose="unloadReportsPhotos"
            modalTitle="Выгрузить zip архив с фотоотчетом"
            hasIcon="zip"
          />
        </>
      ),
    },
    // {
    // 	component: (
    // 		<>
    // 			<HeaderBtn
    // 				content='Выгрузить тандер'
    // 				purpose='unloadReportsTander'
    // 				modalTitle='Выгрузить тандер'
    // 				hasIcon='excel'
    // 			/>
    // 		</>
    // 	),
    // }
  ];

  const filteredAvailableButtons = availableButtons.filter(
    (btn: { component: JSX.Element }) => btn
  );

  const handleClickCheckbox = (name: string) => {
    if (reportsInputValues[name]) {
      const newReportsInputValues = { ...reportsInputValues };
      delete newReportsInputValues[name];
      dispatch(reducerUserReportsInputValues(newReportsInputValues));
    } else {
      dispatch(
        reducerUserReportsInputValues({
          ...reportsInputValues,
          [name]: !reportsInputValues[name],
        })
      );
    }
    dispatch(reducerPending(true));
    dispatch(actionGetUserReports(limit, page)).then(() => {
      dispatch(reducerPending(false));
    });
  };

  useEffect(() => {
    setOverflowedBtnsCount(0);
  }, [reportContracts]);

  useEffect(() => {
    if (
      filteredAvailableButtons.slice(-(overflowedBtnsCount as number)).length +
        filteredAvailableButtons.slice(
          0,
          filteredAvailableButtons.length - overflowedBtnsCount
        ).length >
      filteredAvailableButtons.length
    ) {
      setOverflowedBtnsCount(0);
    }
  }, [filteredAvailableButtons, overflowedBtnsCount]);

  const handleWindowResize = useCallback(() => {
    const allButtonsWidth =
      filteredAvailableButtons.slice(
        0,
        filteredAvailableButtons.length - overflowedBtnsCount
      ).length * buttonWidth;
    const spaceToLeftSideOfFilters =
      headFilterRef.current?.getBoundingClientRect().left;
    const windowWidth = window.innerWidth;

    const isOverflowed = JSON.parse(IS_VISIBLE_SIDEBAR as string)
      ? allButtonsWidth >
        windowWidth - headerFiltersAndSelectWidth - sidebarWidth
      : allButtonsWidth > windowWidth - headerFiltersAndSelectWidth;

    if (isOverflowed) {
      setIsOpenGroupBtn(true);
      setOverflowedBtnsCount((prevValue: number) => {
        if (overflowedBtnsCount === filteredAvailableButtons.length) {
          return prevValue;
        } else return prevValue + 1;
      });
    }

    const isEnoughtSpace = JSON.parse(IS_VISIBLE_SIDEBAR as string)
      ? spaceToLeftSideOfFilters -
          sidebarWidth -
          allButtonsWidth +
          groupBtnWidth >
        minEmptyWidthForNewBtn
      : spaceToLeftSideOfFilters - allButtonsWidth + groupBtnWidth >
        minEmptyWidthForNewBtn;

    if (isEnoughtSpace) {
      setOverflowedBtnsCount((prevValue: number) => {
        if (prevValue <= 1) {
          setIsOpenGroupBtn(false);
          return 0;
        } else return prevValue - 1;
      });
    }
  }, [filteredAvailableButtons, overflowedBtnsCount, sidebarWidth]);

  useEffect(() => {
    handleWindowResize();

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [handleWindowResize]);

  const displayedAvailableBtns = filteredAvailableButtons
    ?.slice(-(overflowedBtnsCount as number))
    ?.map((elem: { component: JSX.Element }, index: number) => {
      return elem && <div key={index}>{elem?.component}</div>;
    });

  const displayedHeadBtns = filteredAvailableButtons
    ?.slice(0, filteredAvailableButtons.length - overflowedBtnsCount)
    ?.map((elem: { component: JSX.Element }, index: number) => {
      return elem && <div key={index}>{elem?.component}</div>;
    });

  return (
    <div className="App">
      <Sidebar />
      <SectionContent
        headBlockFilters={
          <div className="head__block_filters" ref={headFilterRef}>
            <div className="audit__filter_block">
              <span>Тандер</span>
              <input
                defaultChecked={reportsInputValues.tander}
                onChange={() => handleClickCheckbox("tander")}
                type="checkbox"
              />
            </div>
          </div>
        }
        availableButtons={displayedAvailableBtns}
        buttonsContainerRef={buttonsContainerRef}
        headBlockChildren={displayedHeadBtns}
        isOpenGroupBtn={isOpenGroupBtn}
        pageName="reports"
        section="Отчеты"
      >
        <div className="content__main">
          <div className="btns">
            <div className="tab">
              <div className="buttons__btn --active">
                Всего
                <div className="tab__reports_count">
                  {!reportsCount && reportsCount !== 0 ? (
                    <CircleLoader />
                  ) : (
                    reportsCount
                  )}
                </div>
              </div>
            </div>
          </div>
          <UserReports />
        </div>
      </SectionContent>
    </div>
  );
};

export default CustomerReportsSection;
